/**
 * Admin → Screening → Import bookings: the import's progress and result
 * (plan 7.6), kept outside the panel.
 *
 * The result carries the participants' one-time manage links until staff
 * confirm they've saved the downloaded file, or close the result. Keeping
 * it here rather than in the panel means leaving the panel, the Screening
 * tab or the admin page within
 * the app loses nothing: an import still running carries on, and the panel
 * shows its result when it's open again. Reloading or closing the page would
 * lose it, so the browser asks first, and signing out asks too (the app
 * bar). This module has no UI imports, so the app bar can use it cheaply.
 */

import { useEffect, useState } from "react";

const EMPTY = { running: false, progress: null, result: null, linksOffered: false, linksDownloaded: false, error: null };

// Kept across hot reloads in development, so a running import isn't orphaned.
// (Test runners may provide import.meta.hot without its data.)
const hot = import.meta.hot && import.meta.hot.data ? import.meta.hot : null;
const store = (hot && hot.data.store) || { state: EMPTY, listeners: new Set() };
if (hot) hot.data.store = store;

// Set once the user has agreed to sign out: the page is leaving, so the
// browser needn't ask again (a running import could still write its result).
let signingOut = false;

export function setImportState(patch) {
  if (patch.running) signingOut = false;
  store.state = { ...store.state, ...patch };
  syncLeaveWarning();
  store.listeners.forEach((listener) => listener(store.state));
}

/** The import's current state, re-rendering as it changes. */
export function useImportState() {
  const [state, setState] = useState(store.state);
  useEffect(() => {
    store.listeners.add(setState);
    setState(store.state);
    return () => store.listeners.delete(setState);
  }, []);
  return state;
}

/** Whether an import is running, or its links are waiting to be saved. */
export const importPending = () => store.state.running || (!!store.state.result && store.state.linksOffered);

/** Forget the import (after its result has been closed, and in tests). */
export const resetImportState = () => setImportState(EMPTY);

export const confirmDiscardLinks = () =>
  window.confirm("You haven't confirmed that the import's manage links are saved, and they won't be shown again. Discard them?");

/**
 * Before signing out: ask if an import is running or its links wait, and
 * drop it if the user agrees. Signing out removes the user before the page
 * leaves, so the browser's own prompt would come too late.
 */
export function confirmSignOut() {
  if (importPending()) {
    if (!confirmDiscardLinks()) return false;
    resetImportState();
  }
  signingOut = true;
  return true;
}

function warnBeforeLeaving(event) {
  if (signingOut || !importPending()) return;
  event.preventDefault();
  event.returnValue = "";
}

// The browser's prompt is listened for only while an import is running or
// its links wait: a beforeunload listener, even an idle one, can keep the
// page out of the back/forward cache.
let warning = false;
function syncLeaveWarning() {
  if (typeof window === "undefined") return;
  const pending = importPending();
  if (pending && !warning) window.addEventListener("beforeunload", warnBeforeLeaving);
  if (!pending && warning) window.removeEventListener("beforeunload", warnBeforeLeaving);
  warning = pending;
}
syncLeaveWarning(); // an import kept across a hot reload
if (hot) {
  hot.dispose(() => {
    if (warning) window.removeEventListener("beforeunload", warnBeforeLeaving);
  });
}
